import React, { useContext, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { LanguageContext } from "@/App";
import { Menu, X, Globe } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

const Navbar = () => {
  const { language, toggleLanguage } = useContext(LanguageContext);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navItems = {
    en: [
      { name: "Home", path: "/" },
      { name: "About", path: "/about" },
      { name: "Divine Attractions", path: "/divine-attractions" },
      { name: "Gallery", path: "/gallery" },
      { name: "Events", path: "/events" },
      { name: "Donate", path: "/donate" },
      { name: "Contact", path: "/contact" },
    ],
    hi: [
      { name: "होम", path: "/" },
      { name: "हमारे बारे में", path: "/about" },
      { name: "दिव्य आकर्षण", path: "/divine-attractions" },
      { name: "गैलरी", path: "/gallery" },
      { name: "कार्यक्रम", path: "/events" },
      { name: "दान करें", path: "/donate" },
      { name: "संपर्क करें", path: "/contact" },
    ],
  };

  const logo = {
    en: "Sri Siddhachal Sthulabhadra Dham",
    hi: "श्री सिद्धाचल स्थूलभद्र धाम",
  };

  return (
    <nav className="sticky top-0 z-50 backdrop-blur-glass border-b border-amber-200 shadow-sm">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center space-x-3"
            data-testid="navbar-logo"
          >
            {/* <div className="w-12 h-12 bg-gradient-to-br from-amber-600 to-amber-800 rounded-full flex items-center justify-center text-white font-bold text-xl">
              ॐ
            </div> */}
            <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-xl">
              <img src="/ssdlogo.avif" alt="SSD Logo" />
            </div>

            <span className="text-xl md:text-2xl font-bold gradient-text">
              {logo[language]}
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-6">
            {navItems[language].map((item) => (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-link-${item.path}`}
                className={`text-sm font-medium transition-colors hover:text-amber-700 ${
                  location.pathname === item.path
                    ? "text-amber-700 font-semibold"
                    : "text-amber-900"
                }`}
              >
                {item.name}
              </Link>
            ))}

            {/* Language Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  data-testid="language-dropdown"
                >
                  <Globe className="w-4 h-4" />
                  {language === "en" ? "EN" : "हि"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem
                  onClick={() => toggleLanguage("en")}
                  data-testid="lang-en"
                >
                  English
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => toggleLanguage("hi")}
                  data-testid="lang-hi"
                >
                  हिंदी
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex lg:hidden items-center space-x-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  data-testid="mobile-language-dropdown"
                >
                  <Globe className="w-4 h-4" />
                  {language === "en" ? "EN" : "हि"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem
                  onClick={() => toggleLanguage("en")}
                  data-testid="mobile-lang-en"
                >
                  English
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => toggleLanguage("hi")}
                  data-testid="mobile-lang-hi"
                >
                  हिंदी
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-amber-900 p-2"
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden pb-4" data-testid="mobile-menu">
            {navItems[language].map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                data-testid={`mobile-nav-link-${item.path}`}
                className={`block py-3 px-4 text-sm font-medium transition-colors hover:bg-amber-50 rounded ${
                  location.pathname === item.path
                    ? "text-amber-700 bg-amber-50 font-semibold"
                    : "text-amber-900"
                }`}
              >
                {item.name}
              </Link>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
